import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
  ActivityIndicator,
  SafeAreaView,
  Dimensions,
  Platform,
  Image,
  TextInput,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { CommonActions } from '@react-navigation/native';
import { sanitizePhoneInput } from '../utils/phoneInput';
import { getUserProfile } from '../services/authApi';
import { getMyCoupons, formatClaimedCouponForDisplay } from '../services/couponsApi';
import { getPaygiftClaimModalUi } from '../utils/paygiftUserFacingMessage';

const { height } = Dimensions.get('window');
const MODAL_MAX_HEIGHT = height * 0.88;
const HEXPOD_ORANGE = '#e46b29';
const HEXPOD_ORANGE_LIGHT = '#e46b2980';
const POINT_GOLD = '#92400e';
const POINT_GOLD_BG = '#fef3c7';
const POINT_GOLD_BORDER = '#fcd34d';

function formatPoints(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('id-ID');
}

function getBonusPoints(couponInfo) {
  const p = Number(couponInfo?.coupon_poin ?? couponInfo?.hadiah_poin ?? 0);
  return p > 0 ? p : 0;
}

const REWARD_TYPE_LABELS = {
  paygift: 'Voucher Belanja',
  hadiah_produk: 'Hadiah Produk',
  cashback: 'LinkAja',
  digiprosb: 'Voucher Digital',
};

function getRewardType(couponInfo) {
  return couponInfo?.coupon_type || null;
}

function getRewardTypeLabel(couponInfo) {
  const t = getRewardType(couponInfo);
  return (t && REWARD_TYPE_LABELS[t]) || 'Reward HEXPOD';
}

function getValidationHint(couponInfo) {
  const t = getRewardType(couponInfo);
  if (t === 'hadiah_produk') {
    return 'Hadiah produk — setelah klaim, tim kami akan menghubungi Anda untuk pengiriman.';
  }
  if (t === 'cashback') {
    return 'Cashback LinkAja — masukkan nomor LinkAja yang terdaftar di aplikasi. Nomor LinkAja bisa berbeda dengan nomor hp yang terdaftar di Zoolyfe.';
  }
  if (t === 'paygift') {
    return 'Voucher belanja — dapat digunakan melalui menu Kupon setelah diklaim.';
  }
  if (t === 'digiprosb') {
    return 'Voucher digital — setelah klaim, lanjutkan di menu Kupon untuk memasukkan data tujuan dan memproses voucher.';
  }
  return 'Reward eksklusif untuk pengguna device HEXPOD.';
}

const LINKAJA_UNREGISTERED_GUIDANCE = 'Nomor yang kamu masukkan tidak terdaftar di Aplikasi LinkAja. Lanjutkan claim cashback kamu dengan cara mengakses Coupon lalu di tabs Shopping Coupon, klik button View Details, lalu masukkan nomor yang terdaftar dan lanjutkan proses klaimnya.';

const LINKAJA_SHOPPING_COUPON_GUIDANCE = 'Klaim reward berhasil. Lanjutkan cashback LinkAja di menu Kupon → tab Shopping Coupon → View Details, lalu masukkan nomor LinkAja terdaftar.';

const LINKAJA_UNAVAILABLE_CS_MSG = 'Layanan LinkAja sedang tidak tersedia saat ini. Klaim reward kamu sudah kami catat — silakan hubungi Customer Service kami untuk bantuan memproses cashback.';

const REWARD_LINKAJA_UNAVAILABLE_MSG = LINKAJA_UNAVAILABLE_CS_MSG;

function sanitizeRewardErrorMessage(msg) {
  const text = String(msg || '').trim();
  const lower = text.toLowerCase();
  if (!text) {
    return 'Gagal klaim reward. Silakan coba lagi.';
  }
  const needles = [
    'snap_private_key',
    'autentikasi snap',
    'kredensial client',
    'access token',
    'snap_api',
    'private key',
    'do not honor',
    'insufficient balance',
    'insufficient fund',
    'saldo tidak cukup',
    'saldo habis',
    'merchant balance',
  ];
  for (const needle of needles) {
    if (lower.includes(needle)) {
      return REWARD_LINKAJA_UNAVAILABLE_MSG;
    }
  }
  return text;
}

function notifyOpenerCancel() {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.opener) {
    window.opener.postMessage({ type: 'reward_cancelled' }, '*');
  }
}

function notifyOpenerClaimed(json, hexpodOrigin) {
  const payload = {
    type: 'reward_claimed',
    claim_device_id: json?.claim_device_id || null,
    serial_number: json?.serial_number || null,
    cashback_pending: !!json?.cashback_pending,
    reward_awaiting_phone: !!json?.reward_awaiting_phone,
    reward_type: json?.reward_type || null,
    paygift_pending: !!json?.paygift_pending || isPaygiftResultPending(
      { reward_type: json?.reward_type || 'paygift' },
      json
    ),
    paygift_pending_reason: json?.paygift_pending_reason ?? null,
    linkaja_not_registered: !!json?.linkaja_not_registered,
    linkaja_snap_deferred: !!json?.linkaja_snap_deferred,
    linkaja_service_unavailable: !!json?.linkaja_service_unavailable,
    continue_in_shopping_coupon: !!json?.continue_in_shopping_coupon,
    message: json?.message || null,
    ts: Date.now(),
  };

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    if (window.opener) {
      try {
        window.opener.postMessage(payload, hexpodOrigin || '*');
      } catch (_) {
        window.opener.postMessage(payload, '*');
      }
    }
    try {
      localStorage.setItem('hexpod_reward_claimed_pending', JSON.stringify(payload));
    } catch (_) {}
  }
}

function LinkAjaPhoneInput({ value, onChangeText, editable = true, style, placeholder = '08xxxxxxxxxx' }) {
  if (Platform.OS === 'web') {
    return (
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        maxLength={15}
        value={value}
        placeholder={placeholder}
        disabled={!editable}
        onChange={(e) => onChangeText(sanitizePhoneInput(e.target.value))}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: '#DDDDDD',
          borderRadius: 10,
          padding: '12px 14px',
          fontSize: 16,
          color: '#222',
          backgroundColor: editable ? '#FAFAFA' : '#F0F0F0',
          outline: 'none',
          WebkitAppearance: 'none',
          ...(style?.marginTop != null ? { marginTop: style.marginTop } : {}),
        }}
      />
    );
  }

  return (
    <TextInput
      style={style}
      placeholder={placeholder}
      placeholderTextColor="#999"
      keyboardType="number-pad"
      inputMode="numeric"
      maxLength={15}
      value={value}
      onChangeText={onChangeText}
      editable={editable}
    />
  );
}

/** Web: native overflow scroll (RN ScrollView maxHeight unreliable on mobile web). Native: ScrollView. */
function RewardModalScroll({ children }) {
  if (Platform.OS === 'web') {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          maxHeight: 'min(88vh, calc(100vh - 32px))',
          margin: 'auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
          borderRadius: 16,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          flexShrink: 0,
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <View style={styles.modalContent}>
      <ScrollView
        style={[styles.modalScrollArea, { maxHeight: MODAL_MAX_HEIGHT }]}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        nestedScrollEnabled={true}
        bounces={true}
        contentContainerStyle={styles.modalScrollContent}
      >
        {children}
      </ScrollView>
    </View>
  );
}

function getLinkAjaPendingGuidance(json) {
  if (json?.linkaja_not_registered) {
    return LINKAJA_UNREGISTERED_GUIDANCE;
  }
  if (json?.linkaja_service_unavailable) {
    return json?.message || LINKAJA_UNAVAILABLE_CS_MSG;
  }
  if (json?.linkaja_snap_deferred || json?.continue_in_shopping_coupon) {
    return json?.message || LINKAJA_SHOPPING_COUPON_GUIDANCE;
  }
  return json?.message
    || 'Klaim berhasil. Lengkapi nomor LinkAja di detail kupon untuk menerima cashback.';
}

function getSuccessResultCopy(rewardType, json) {
  if (json?.already_claimed) {
    return 'Reward sudah pernah diklaim sebelumnya.';
  }
  if (rewardType === 'hadiah_produk') {
    return 'Pastikan nomor handphone mu aktif, kami akan menghubungimu untuk memproses hadiah produk dan melakukan pengiriman';
  }
  if (rewardType === 'cashback') {
    if (json?.cashback_pending) {
      return getLinkAjaPendingGuidance(json);
    }
    const amt = json?.cashback_amount;
    return amt
      ? `Cashback LinkAja Rp ${Number(amt).toLocaleString('id-ID')} berhasil dikirim.`
      : 'Cashback LinkAja berhasil dikirim.';
  }
  if (rewardType === 'paygift' && (
    json?.paygift_pending
    || json?.paygift_pending_reason
    || json?.paygift_claim_pending
    || !json?.voucher_url
  )) {
    return getPaygiftClaimModalUi({
      paygift: true,
      tipe_kupon: 'paygift',
      paygift_pending_reason: json?.paygift_pending_reason,
      voucher_url: json?.voucher_url,
    }).headerMessage;
  }
  if (rewardType === 'digiprosb' || json?.reward_awaiting_phone) {
    return json?.message
      || 'Klaim reward berhasil. Lanjutkan di Kupon (Shopping Coupon) untuk memasukkan data tujuan dan memproses voucher.';
  }
  return json?.message || 'Reward berhasil diklaim!';
}

function isPaygiftResultPending(result, json) {
  if ((result?.reward_type || json?.reward_type) !== 'paygift') return false;
  if (result?.paygiftClaimPending || json?.paygift_claim_pending || json?.paygift_pending) return true;
  if (result?.paygift_pending_reason || json?.paygift_pending_reason) return true;
  const url = result?.voucher_url ?? json?.voucher_url;
  return !(url && String(url).trim());
}

function shouldOfferCouponDetail(result) {
  if (result?.type !== 'success') return false;
  if (
    result?.reward_type === 'cashback'
    && result?.cashback_pending
    && result?.claimed_without_linkaja
  ) {
    return true;
  }
  if (
    result?.reward_type === 'paygift'
    && isPaygiftResultPending(result, result)
    && Number(result?.claim_detail_id || 0) > 0
  ) {
    return true;
  }
  if (
    (result?.reward_type === 'digiprosb' || result?.reward_awaiting_phone)
    && Number(result?.claim_detail_id || 0) > 0
  ) {
    return true;
  }
  return false;
}

function buildCashbackCouponNavParams(couponInfo, result) {
  const couponId = Number(result?.coupon_id || 0);
  const claimId = Number(result?.claim_detail_id || 0);
  return {
    id: couponId || undefined,
    claim_id: claimId || undefined,
    claimId: claimId || undefined,
    is_claimed: true,
    cashback: true,
    cashback_pending: true,
    tipe_kupon: 'cashback',
    type: 'cashback',
    title: couponInfo?.coupon_name || 'Cashback LinkAja',
    couponCode: couponInfo?.coupon_code || '',
    coupon_code: couponInfo?.coupon_code || '',
    image: couponInfo?.coupon_image_url || null,
    coupon_image_url: couponInfo?.coupon_image_url || null,
  };
}

function buildPaygiftCouponNavParams(couponInfo, result) {
  const couponId = Number(result?.coupon_id || 0);
  const claimId = Number(result?.claim_detail_id || 0);
  return {
    id: couponId || undefined,
    claim_id: claimId || undefined,
    claimId: claimId || undefined,
    is_claimed: true,
    paygift: true,
    isPaygift: true,
    tipe_kupon: 'paygift',
    type: 'paygift',
    title: couponInfo?.coupon_name || 'Voucher Belanja',
    nama_kupon: couponInfo?.coupon_name || 'Voucher Belanja',
    couponCode: couponInfo?.coupon_code || '',
    coupon_code: couponInfo?.coupon_code || '',
    kode_kupon: couponInfo?.coupon_code || '',
    image: couponInfo?.coupon_image_url || null,
    coupon_image_url: couponInfo?.coupon_image_url || null,
    paygift_pending_reason: result?.paygift_pending_reason ?? null,
    voucher_url: result?.voucher_url ?? null,
    paygift_trx_id: result?.paygift_trx_id ?? null,
  };
}

function buildDigiprosbCouponNavParams(couponInfo, result) {
  const couponId = Number(result?.coupon_id || 0);
  const claimId = Number(result?.claim_detail_id || 0);
  return {
    id: couponId || undefined,
    claim_id: claimId || undefined,
    claimId: claimId || undefined,
    is_claimed: true,
    tipe_kupon: 'digiprosb',
    type: 'digiprosb',
    title: couponInfo?.coupon_name || 'Voucher Digital',
    nama_kupon: couponInfo?.coupon_name || 'Voucher Digital',
    couponCode: couponInfo?.coupon_code || '',
    coupon_code: couponInfo?.coupon_code || '',
    kode_kupon: couponInfo?.coupon_code || '',
    image: couponInfo?.coupon_image_url || null,
    coupon_image_url: couponInfo?.coupon_image_url || null,
    reward_awaiting_phone: true,
    tampil_barcode: !!result?.tampil_barcode,
    digiprosb_dest_type: result?.digiprosb_dest_type ?? null,
    dest_form: result?.dest_form ?? null,
  };
}

async function resolveCouponForDetail(couponInfo, result) {
  const claimDetailId = Number(result?.claim_detail_id || 0);
  if (claimDetailId > 0) {
    try {
      const resp = await getMyCoupons({ limit: 50 });
      const list = resp?.data?.coupons || [];
      const match = list.find((c) => Number(c.claim_id) === claimDetailId);
      if (match) {
        return formatClaimedCouponForDisplay(match);
      }
    } catch (_) {
      // fallback to minimal coupon object below
    }
  }
  if (result?.reward_type === 'paygift') {
    return buildPaygiftCouponNavParams(couponInfo, result);
  }
  if (result?.reward_type === 'digiprosb' || result?.reward_awaiting_phone) {
    return buildDigiprosbCouponNavParams(couponInfo, result);
  }
  return buildCashbackCouponNavParams(couponInfo, result);
}

function PointIcon({size = 16, color = "#FFFFFF"}) {
  return (
    <Image 
      source={require('../../assets/icons/z-point-solid.svg')} 
      style={{ 
        width: size, 
        height: size, 
        tintColor: color
      }}
      resizeMode="contain"
    />
  )
}

/**
* DeviceRewardScreen
*
* Post-SSO screen triggered by "Get Reward" web-hexpod.
* Shows Paygift coupon validation popup (similar to ScanScreen QR flow):
* 1. Validation Modal → user reviews coupon → "Claim Reward" / "Cancel"
* 2. Result Modal → success or error
*/

export default function DeviceRewardScreen() {
  const navigation = useNavigation();
  const route      = useRoute();

  const { reward_token, hexpod_url } = route.params || {};

  const [loading, setLoading]                             = useState(false);
  const [fetchingInfo, setFetchingInfo]                   = useState(true);
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [resultModalVisible, setResultModalVisible]         = useState(false);
  const [result, setResult]                               = useState(null);
  const [couponInfo, setCouponInfo]                       = useState(null);
  const [linkajaNumber, setLinkajaNumber]                 = useState('');
  const [currentUserPoints, setCurrentUserPoints]         = useState(null);
  const hexpodBaseUrl = (hexpod_url || 'http://localhost:8000').replace(/\/$/, '');
  const rewardFlowFinishedRef = useRef(false);

  useEffect(() => {
    rewardFlowFinishedRef.current = false;
    setLoading(false);
    setFetchingInfo(true);
    setValidationModalVisible(false);
    setResultModalVisible(false);
    setResult(null);
    setCouponInfo(null);
    setLinkajaNumber('');
    setCurrentUserPoints(null);
  }, [reward_token, hexpodBaseUrl]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (!rewardFlowFinishedRef.current) {
        notifyOpenerCancel();
      }
    };
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('beforeunload', onBeforeUnload);
    }
    return () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', onBeforeUnload);
      }
      if (!rewardFlowFinishedRef.current) {
        notifyOpenerCancel();
      }
    };
  }, []);

  // Fetch coupon info + saldo poin user untuk popup reward
  useEffect(() => {
    const fetchCouponInfo = async () => {
      if (!reward_token) {
        setResult({ type: 'error', message: 'Token reward tidak ditemukan. Silakan kembali ke halaman device.' });
        setFetchingInfo(false);
        setResultModalVisible(true);
        return;
      }
      let loadError = null;
      try {
        const [infoRes, profileRes] = await Promise.all([
          fetch(`${hexpodBaseUrl}/device/reward-info?reward_token=${encodeURIComponent(reward_token)}`),
          getUserProfile().catch(() => null),
        ]);
        if (profileRes?.data?.points != null) {
          setCurrentUserPoints(Number(profileRes.data.points) || 0);
        }
        if (!infoRes.ok) {
          throw new Error(`HTTP ${infoRes.status}`);
        }
        const json = await infoRes.json();
        if (json.success && json.coupon_info) {
          setCouponInfo(json.coupon_info);
        } else {
          loadError = json.message || 'Gagal memuat info reward.';
        }
      } catch (e) {
        loadError =
          'Tidak dapat memuat info reward dari Hexpod. Pastikan web-hexpod berjalan di ' +
          hexpodBaseUrl +
          ' dan origin zoolyfe-club diizinkan CORS.';
      }
      setFetchingInfo(false);
      if (loadError) {
        setResult({ type: 'error', message: sanitizeRewardErrorMessage(loadError) });
        setResultModalVisible(true);
      } else {
        setValidationModalVisible(true);
      }
    };
    fetchCouponInfo();
  }, [reward_token, hexpodBaseUrl]);

  const handleCancel = () => {
    rewardFlowFinishedRef.current = true;
    setValidationModalVisible(false);
    notifyOpenerCancel();
    navigation.dispatch(
      CommonActions.reset({ index: 0, routes: [{ name: 'MainTabs' }] })
    );
  };

  const handleClaimReward = async () => {
    if (!reward_token) {
      setResult({ type: 'error', message: 'Token reward tidak ditemukan. Silakan kembali ke halaman device.' });
      setValidationModalVisible(false);
      setResultModalVisible(true);
      return;
    }

    setLoading(true);
    try {
      // Send device timezone for dynamic format WIB/WITA/WIT
      const deviceTimezone = Intl.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || 'Asia/Jakarta';
      const payload = { reward_token, timezone: deviceTimezone };
      const linkajaDigits = linkajaNumber.trim();
      const rewardType = getRewardType(couponInfo);
      const claimedWithoutLinkaja = rewardType === 'cashback' && !linkajaDigits;
      if (rewardType === 'cashback' && linkajaDigits) {
        if (linkajaDigits.length < 10) {
          setResult({ type: 'error', message: 'Nomor LinkAja minimal 10 digit (contoh: 08xxxxxxxxxx).' });
          setValidationModalVisible(false);
          setResultModalVisible(true);
          setLoading(false);
          return;
        }
        payload.linkaja_number = linkajaDigits;
      }
      const res = await fetch(`${hexpodBaseUrl}/device/process-reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (json.success) {
        rewardFlowFinishedRef.current = true;
        notifyOpenerClaimed(json, hexpodBaseUrl);
        setResult({
          type:         'success',
          message:      getSuccessResultCopy(json.reward_type || getRewardType(couponInfo), json),
          reward_type:  json.reward_type || getRewardType(couponInfo) || 'paygift',
          paygift_trx_id: json.paygift_trx_id,
          paygift_pending_reason: json.paygift_pending_reason ?? null,
          voucher_url: json.voucher_url ?? null,
          paygiftClaimPending: isPaygiftResultPending(
            { reward_type: json.reward_type || getRewardType(couponInfo) || 'paygift' },
            json
          ),
          product_name: json.product_name,
          cashback_amount: json.cashback_amount,
          cashback_pending: !!json.cashback_pending,
          reward_awaiting_phone: !!json.reward_awaiting_phone,
          tampil_barcode: !!json.tampil_barcode,
          digiprosb_dest_type: json.digiprosb_dest_type ?? null,
          dest_form: json.dest_form ?? null,
          linkaja_not_registered: !!json.linkaja_not_registered,
          linkaja_snap_deferred: !!json.linkaja_snap_deferred,
          continue_in_shopping_coupon: !!json.continue_in_shopping_coupon,
          validation_code: json.validation_code ?? null,
          points_awarded: (json.points_awarded ?? getBonusPoints(couponInfo)) || null,
          total_points_after: json.total_points_after ?? null,
          claim_detail_id: json.claim_detail_id ?? null,
          coupon_id: json.coupon_id ?? null,
          claimed_without_linkaja: claimedWithoutLinkaja && !!json.cashback_pending,
        });
      } else {
        setResult({ type: 'error', message: sanitizeRewardErrorMessage(json.message || 'Gagal klaim reward.') });
      }
    } catch (e) {
      setResult({
        type: 'error',
        message:
          'Tidak dapat terhubung ke Hexpod (' +
          hexpodBaseUrl +
          '). Periksa CORS / pastikan server hexpod aktif.',
      });
    } finally {
      setLoading(false);
      setValidationModalVisible(false);
      setResultModalVisible(true);
    }
  };

  const handleResultClose = async () => {
    setResultModalVisible(false);
    if (shouldOfferCouponDetail(result)) {
      const coupon = await resolveCouponForDetail(couponInfo, result);
      const detailScreen =
        result?.reward_type === 'paygift' ? 'PaygiftCouponDetails' : 'CouponDetails';
      // Digiprosb / cashback use CouponDetails (destination / LinkAja form)
      navigation.dispatch(
        CommonActions.reset({
          index: 1,
          routes: [
            { name: 'MainTabs' },
            { name: detailScreen, params: { coupon } },
          ],
        })
      );
      return;
    }
    if (result?.type === 'success') {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [
            {
              name: 'MainTabs',
              state: {
                routes: [
                  { name: 'Home' },
                  { name: 'Coupons' },
                  { name: 'TukarPoin' },
                  { name: 'Estamps' },
                  { name: 'Profile' },
                ],
                index: 1, // Coupons = Shopping Coupon
              },
            },
          ],
        })
      );
    } else {
      if (!rewardFlowFinishedRef.current) {
        notifyOpenerCancel();
      }
      navigation.dispatch(
        CommonActions.reset({ index: 0, routes: [{ name: 'MainTabs' }] })
      );
    }
  };

  // ── Validation Modal (Popup 1) ─────────────────────────────────────────
  const bonusPoints = getBonusPoints(couponInfo);

  const renderValidationContent = () => (
    <RewardModalScroll>
        {/* Header gradient — Hexpod orange */}
        <LinearGradient
          colors={[HEXPOD_ORANGE, HEXPOD_ORANGE_LIGHT]}
          style={styles.resultHeader}
        >
          <View style={styles.iconCircle}>
            <Image
              source={require('../../assets/favicon-dark.svg')}
              style={styles.logoIcon}
              resizeMode="contain"
            />
          </View>
          {couponInfo?.coupon_image_url ? (
            <Image
              source={{ uri: couponInfo.coupon_image_url }}
              style={styles.couponBanner}
              resizeMode="cover"
            />
          ) : null}
          <Text style={styles.resultTitle}>Selamat! 🎉</Text>
          <Text style={styles.resultMessage}>
            Kamu mendapatkan reward eksklusif dari HEXPOD!{'\n'}
            Reward sudah siap untuk diklaim.
          </Text>
        </LinearGradient>

        <View style={styles.resultDetails}>
          {/* Voucher Information */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Voucher Information</Text>
            <Text style={styles.couponName}>
              {couponInfo?.coupon_name || getRewardTypeLabel(couponInfo)}
            </Text>
            <Text style={styles.couponDesc}>
              {getValidationHint(couponInfo)}
            </Text>
          </View>

          {/* Rewards section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Rewards</Text>
            <View style={styles.rewardItem}>
              <Ionicons name="gift" size={20} color="#9C27B0" />
              <Text style={styles.rewardText}>
                {getRewardTypeLabel(couponInfo)}
                {couponInfo?.coupon_name ? ` — ${couponInfo.coupon_name}` : ''}
              </Text>
            </View>
            {bonusPoints > 0 && (
              <View style={styles.pointBonusCard}>
                <PointIcon size={20} color={POINT_GOLD}/>
                <View style={styles.pointBonusTextWrap}>
                  <Text style={styles.pointBonusTitle}>Bonus Poin Zoolyfe</Text>
                  <Text style={styles.pointBonusValue}>+{formatPoints(bonusPoints)} poin</Text>
                  <Text style={styles.pointBonusHint}>
                    Poin ditambahkan ke saldo Point Reward setelah klaim berhasil.
                  </Text>
                </View>
              </View>
            )}
          </View>

          {/* Coupon Details */}
          <View style={[styles.section, { borderBottomWidth: 0, marginBottom: 0, paddingBottom: 0 }]}>
            <Text style={styles.sectionTitle}>Coupon Details</Text>
            <Text style={styles.couponDesc}>
              {couponInfo?.coupon_name || getRewardTypeLabel(couponInfo)}
            </Text>
            <Text style={styles.couponExpiry}>Reward hanya dapat diklaim satu kali per device</Text>
          </View>
        </View>

        {getRewardType(couponInfo) === 'cashback' && (
          <View style={styles.linkajaInputFooter}>
            <Text style={styles.linkajaInputLabel}>Nomor LinkAja (nomor hp yang terdaftar di aplikasi LinkAja Anda)</Text>
            <LinkAjaPhoneInput
              style={styles.linkajaInput}
              value={linkajaNumber}
              onChangeText={(text) => setLinkajaNumber(sanitizePhoneInput(text))}
              editable={!loading}
            />
            <Text style={styles.linkajaInputHint}>
              Kosongkan jika ingin mengisi nanti di detail kupon. Pastikan Anda sudah memiliki aplikasi e-wallet LinkAja sebelum klaim reward ini.
            </Text>
          </View>
        )}

        <View style={styles.modalFooterInScroll}>
          <View style={styles.actionRow}>
            <TouchableOpacity
              style={[styles.actionBtn, styles.cancelBtn]}
              onPress={handleCancel}
              disabled={loading}
            >
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionBtn, styles.claimBtn, loading && { opacity: 0.7 }]}
              onPress={handleClaimReward}
              disabled={loading}
            >
              {loading
                ? <ActivityIndicator size="small" color="#FFFFFF" />
                : <Text style={styles.claimBtnText}>Klaim Reward</Text>
              }
            </TouchableOpacity>
          </View>
        </View>
    </RewardModalScroll>
  );

  const renderValidationModal = () => {
    if (!validationModalVisible) {
      return null;
    }

    if (Platform.OS === 'web') {
      return (
        <View style={styles.webModalRoot} pointerEvents="auto">
          {renderValidationContent()}
        </View>
      );
    }

    return (
      <Modal
        animationType="slide"
        transparent={true}
        visible={validationModalVisible}
        onRequestClose={handleCancel}
      >
        <View style={styles.modalOverlay}>
          {renderValidationContent()}
        </View>
      </Modal>
    );
  };

  // ── Result Modal (Popup 2) ─────────────────────────────────────────────
  const renderResultContent = () => (
    <RewardModalScroll>
        {result && (
          <>
            <LinearGradient
              colors={result.type === 'success' ? [HEXPOD_ORANGE, '#f0925a'] : ['#F44336', '#F4433680']}
              style={styles.resultHeader}
            >
              <View style={styles.iconCircle}>
                <Ionicons
                  name={result.type === 'success' ? 'checkmark-circle' : 'close-circle'}
                  size={48}
                  color={result.type === 'success' ? HEXPOD_ORANGE : '#F44336'}
                />
              </View>
              <Text style={styles.resultTitle}>
                {result.type === 'success'
                  ? (result.paygiftClaimPending
                      ? getPaygiftClaimModalUi({
                          paygift: true,
                          tipe_kupon: 'paygift',
                          paygift_pending_reason: result.paygift_pending_reason,
                          voucher_url: result.voucher_url,
                        }).title
                      : 'Klaim Berhasil!')
                  : 'Klaim Gagal'}
              </Text>
              <Text style={styles.resultMessage}>
                {result.type === 'success' ? result.message : result.message}
              </Text>
            </LinearGradient>

            {result.type === 'success' && (
              <View style={styles.resultDetails}>
                <View style={{ paddingHorizontal: 4 }}>
                  <View style={styles.rewardAllSection}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                      <Ionicons name="gift" size={20} color={HEXPOD_ORANGE} style={{ marginRight: 8 }} />
                      <Text style={styles.rewardAllTitle}>Reward Kamu</Text>
                    </View>
                    <View style={styles.divider} />
                    {!!result.paygiftClaimPending && (
                      <Text style={styles.voucherNote}>
                        {getPaygiftClaimModalUi({
                          paygift: true,
                          tipe_kupon: 'paygift',
                          paygift_pending_reason: result.paygift_pending_reason,
                          voucher_url: result.voucher_url,
                        }).queueNotice}
                      </Text>
                    )}
                    <Text style={styles.voucherName}>
                      {couponInfo?.coupon_name || REWARD_TYPE_LABELS[result.reward_type] || 'Reward HEXPOD'}
                    </Text>
                    {result.reward_type === 'paygift' && (
                      <Text style={styles.voucherNote}>Voucher berlaku 30 hari setelah diklaim.</Text>
                    )}
                    {result.reward_type === 'hadiah_produk' && result.product_name && (
                      <Text style={styles.voucherNote}>Produk: {result.product_name}</Text>
                    )}
                    {result.reward_type === 'cashback' && result.cashback_amount != null && (
                      <Text style={styles.voucherNote}>
                        Nominal: Rp {Number(result.cashback_amount).toLocaleString('id-ID')}
                      </Text>
                    )}
                  </View>
                  {(result.points_awarded > 0 || bonusPoints > 0) && (
                    <>
                      <View style={styles.divider} />
                      <View style={styles.pointBonusCard}>
                        <PointIcon size={22} color={POINT_GOLD}/>
                        <View style={styles.pointBonusTextWrap}>
                          <Text style={styles.pointBonusTitle}>Bonus Poin Zoolyfe</Text>
                          <Text style={styles.pointBonusValue}>
                            +{formatPoints(result.points_awarded || bonusPoints)} poin ditambahkan
                          </Text>
                          {(result.total_points_after != null
                            || (currentUserPoints != null && (result.points_awarded || bonusPoints))) && (
                            <Text style={styles.pointTotalAfter}>
                              Total poin kamu sekarang:{' '}
                              <Text style={styles.pointTotalAfterValue}>
                                {formatPoints(
                                  result.total_points_after != null
                                    ? result.total_points_after
                                    : currentUserPoints + (result.points_awarded || bonusPoints)
                                )}{' '}
                                poin
                              </Text>
                            </Text>
                          )}
                        </View>
                      </View>
                    </>
                  )}
                  <View style={styles.divider} />
                  <View style={styles.rewardAllSection}>
                    <Text style={styles.statusTitle}>Status</Text>
                    <Text style={styles.statusValue}>
                      {result.reward_type === 'hadiah_produk'
                        ? (result.validation_code ? `Kode validasi: ${result.validation_code}` : 'Hadiah produk diklaim')
                        : result.reward_type === 'cashback'
                          ? (result.cashback_pending ? 'Menunggu nomor LinkAja' : 'Cashback terkirim')
                          : result.paygiftClaimPending
                            ? getPaygiftClaimModalUi({
                                paygift: true,
                                tipe_kupon: 'paygift',
                                paygift_pending_reason: result.paygift_pending_reason,
                                voucher_url: result.voucher_url,
                              }).statusLabel
                            : 'Siap digunakan'}
                    </Text>
                    <Text style={styles.statusNote}>
                      {result.reward_type === 'hadiah_produk'
                        ? 'Simpan kode validasi. CS HEXPOD akan mencocokkannya di History Klaim Reward sebelum pengiriman manual.'
                        : result.reward_type === 'cashback'
                          ? (result.cashback_pending
                            ? getLinkAjaPendingGuidance(result)
                            : 'Saldo LinkAja telah dikirim ke nomor yang kamu masukkan.')
                          : result.paygiftClaimPending
                            ? getPaygiftClaimModalUi({
                                paygift: true,
                                tipe_kupon: 'paygift',
                                paygift_pending_reason: result.paygift_pending_reason,
                                voucher_url: result.voucher_url,
                              }).usageNote
                            : 'Voucher dapat digunakan melalui menu Kupon - Shopping Coupon'}
                    </Text>
                  </View>
                </View>
              </View>
            )}
          </>
        )}

        {result && (
          <View style={styles.modalFooterInScroll}>
            <TouchableOpacity
              style={[styles.continueBtn, { backgroundColor: result.type === 'success' ? HEXPOD_ORANGE : '#F44336' }]}
              onPress={handleResultClose}
            >
              <Text style={styles.continueBtnText}>
                {shouldOfferCouponDetail(result) ? 'Lihat Detail' : 'Continue'}
              </Text>
            </TouchableOpacity>
          </View>
        )}
    </RewardModalScroll>
  );

  const renderResultModal = () => {
    if (!resultModalVisible) {
      return null;
    }

    if (Platform.OS === 'web') {
      return (
        <View style={styles.webModalRoot} pointerEvents="auto">
          {renderResultContent()}
        </View>
      );
    }

    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={resultModalVisible}
        onRequestClose={handleResultClose}
      >
        <View style={styles.modalOverlay}>
          {renderResultContent()}
        </View>
      </Modal>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      {fetchingInfo ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={HEXPOD_ORANGE} />
          <Text style={styles.loadingText}>Memuat reward...</Text>
        </View>
      ) : null}
      {renderValidationModal()}
      {renderResultModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  webModalRoot: {
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      minWidth: '100vw',
      height: '100%',
      minHeight: '100vh',
      zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
      boxSizing: 'border-box',
      overflow: 'hidden',
    } : {}),
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
    flexDirection: 'column',
  },
  modalScrollArea: {
    ...(Platform.OS === 'web' ? {
      overflowY: 'scroll',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
    } : {}),
  },
  modalScrollContent: {
    flexGrow: 1,
    paddingBottom: 16,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  resultHeader: {
    padding: 30,
    alignItems: 'center',
  },
  iconCircle: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  logoIcon: {
    width: 36,
    height: 42,
  },
  couponBanner: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    marginBottom: 12,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  resultTitle: {
    color: '#222',
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  resultMessage: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
  },
  resultDetails: {
    padding: 25,
  },
  section: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  couponName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#222',
    marginBottom: 4,
  },
  couponDesc: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  couponCode: {
    fontSize: 13,
    color: HEXPOD_ORANGE,
    fontWeight: '600',
    marginTop: 4,
  },
  couponExpiry: {
    fontSize: 13,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 6,
  },
  rewardItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  rewardText: {
    fontSize: 15,
    color: '#333',
    marginLeft: 10,
    fontWeight: '500',
    flex: 1,
  },
  pointBonusCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 12,
    padding: 14,
    backgroundColor: POINT_GOLD_BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: POINT_GOLD_BORDER,
    gap: 10,
  },
  pointBonusTextWrap: {
    flex: 1,
  },
  pointBonusTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: POINT_GOLD,
    marginBottom: 4,
  },
  pointBonusValue: {
    fontSize: 17,
    fontWeight: '700',
    color: '#78350f',
    marginBottom: 4,
  },
  pointBonusHint: {
    fontSize: 12,
    color: '#a16207',
    lineHeight: 18,
  },
  pointTotalAfter: {
    fontSize: 14,
    color: '#78350f',
    marginTop: 4,
    lineHeight: 20,
  },
  pointTotalAfterValue: {
    fontWeight: '700',
    color: POINT_GOLD,
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  actionBtn: {
    flex: 1,
    paddingVertical: 15,
    borderRadius: 10,
    alignItems: 'center',
  },
  cancelBtn: {
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#DDDDDD',
  },
  cancelBtnText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  claimBtn: {
    backgroundColor: HEXPOD_ORANGE,
  },
  claimBtnText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  // Result modal styles
  rewardAllSection: {
    paddingVertical: 12,
  },
  rewardAllTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 4,
  },
  voucherName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#222',
    marginTop: 8,
    marginBottom: 6,
  },
  voucherNote: {
    fontSize: 14,
    color: '#666',
  },
  statusTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 6,
  },
  statusValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  statusNote: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  modalFooterInScroll: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    backgroundColor: '#FFFFFF',
  },
  linkajaInputWrap: {
    marginTop: 16,
  },
  linkajaInputFooter: {
    paddingHorizontal: 25,
    paddingTop: 12,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    backgroundColor: '#FFFFFF',
  },
  linkajaInputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  linkajaInput: {
    borderWidth: 1,
    borderColor: '#DDDDDD',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: '#222',
    backgroundColor: '#FAFAFA',
  },
  linkajaInputHint: {
    fontSize: 12,
    color: '#888',
    marginTop: 8,
    lineHeight: 18,
  },
  continueBtn: {
    paddingVertical: 15,
    borderRadius: 12,
    alignItems: 'center',
  },
  continueBtnText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
});
